import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BotaoMenuMobile from '../components/common/BotaoMenuMobile';
import { modoMobileCompleto } from '../utils/modoMobileCompleto';
import { usePermissoes } from '../contexts/PermissoesContext';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { cacheMesas, getMesasCache, cacheComanda, getComandaCache } from '../services/offlineDb';
import { fecharComEsc } from '../utils/impressaoVenda';
import { larguraEfetivaCSS } from '../utils/impressaoMargem';
import s from './MesasMobile.module.css';
import { BRAND_MARCA_CURTA } from '../brand';

const MSG_OFFLINE = 'Essa ação exige conexão com a internet.';

const fmtBRL = v =>
  Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Mesma paleta do F1 Mesas/Comandas do PDV (ModalMesasComandas.jsx) — um
// garçom que também vê o PDV precisa reconhecer a mesma cor pro mesmo status.
const CORES = {
  livre: '#34C759', ocupada: '#8B5CF6', aguardando: '#F59E0B',
  reservada: '#2563EB', limpeza: '#EF4444',
};
const LABEL_STATUS = {
  livre: 'Livre', ocupada: 'Ocupada', aguardando: 'Fechando',
  reservada: 'Reservada', limpeza: 'Limpeza',
};

function elapsedStr(desde, agora) {
  if (!desde) return '';
  const ms = Math.max(0, agora - new Date(desde).getTime());
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m} min`;
}

const FORMAS_PAGAMENTO = ['Dinheiro', 'Pix', 'Cartão Débito', 'Cartão Crédito'];

/**
 * Versão mobile do F1 Mesas/Comandas — pra onde o login do usuário Garçom
 * (checkbox em Cadastro de Usuários) cai direto quando acessado pelo
 * celular (ver App.jsx GuardaMobile). No desktop o mesmo usuário abre o
 * PDV normal com o F1 já aberto (ver PDV.jsx); aqui é uma tela própria,
 * enxuta, sem o resto do PDV — só o necessário pra anotar o pedido na
 * mesa e fechar a conta.
 *
 * 3 telas (a mesma página troca de "modo" em vez de navegar de rota):
 *   'grade'       → grade de mesas/comandas (toggle MESA/COMANDA, legenda
 *                   de status, botão ABRIR MESA fixo embaixo)
 *   'atendimento' → mesa aberta: 2 abas (Produtos / Comanda) na mesma
 *                   tela, pra caber os itens a lançar E os já lançados +
 *                   total sem precisar trocar de tela toda hora
 * Pagamento (Finalizar Venda) é uma folha simplificada só com as formas
 * que não exigem cliente identificado (Dinheiro/Pix/Débito/Crédito à
 * vista) — crediário, boleto e pontos fidelidade continuam exclusivos do
 * PDV de balcão.
 */
export default function MesasMobile() {
  const { usuario, logout } = useAuth();
  const { pronto, permitido } = usePermissoes();
  const online = useOnlineStatus();

  const [tela, setTela] = useState('grade'); // 'grade' | 'atendimento'
  const [abaAtendimento, setAbaAtendimento] = useState('produtos'); // 'produtos' | 'comanda'

  const [tipoVenda, setTipoVenda] = useState('mesa');
  const [mesas, setMesas] = useState([]);
  const [mesaSelId, setMesaSelId] = useState(null);
  const [comanda, setComanda] = useState(null);
  const [itemSelId, setItemSelId] = useState(null);
  const [desconto, setDesconto] = useState(0);

  const [produtos, setProdutos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [grupoSelId, setGrupoSelId] = useState(null);
  const [busca, setBusca] = useState('');

  const [processando, setProcessando] = useState(false);
  const abrindoRef = useRef(false);
  const [erro, setErro] = useState('');
  const [lendoPeso, setLendoPeso] = useState(false);
  const [agora, setAgora] = useState(Date.now());
  const [formAbertura, setFormAbertura] = useState(null); // {numPessoas, nomeCliente} ou null
  const [pagamentoAberto, setPagamentoAberto] = useState(false);
  const [finalizando, setFinalizando] = useState(false);

  const buscaRef = useRef(null);
  const mesaSel = mesas.find(m => m.id === mesaSelId) || null;
  const numFmt = n => String(n).padStart(tipoVenda === 'mesa' ? 2 : 3, '0');

  const orgId = usuario?.organizacao_id;

  const carregarMesas = useCallback((tipo) => {
    api.get('/mesas', { params: { tipo: tipo === 'individual' ? 'comanda' : 'mesa' } })
      .then(r => { setMesas(r.data || []); cacheMesas(r.data || []); })
      .catch(async (err) => {
        if (err.response) { setErro('Erro ao carregar as mesas.'); return; }
        // Nunca de outra organização (mesmo dispositivo pode operar mais de
        // uma loja no mesmo navegador).
        setMesas(await getMesasCache(orgId));
      });
  }, [orgId]);

  useEffect(() => {
    carregarMesas('mesa');
    api.get('/grupos').then(r => setGrupos(r.data || [])).catch(() => {});
    api.get('/produtos', { params: { todos: 'true' } }).then(r => setProdutos(r.data || [])).catch(() => {});
  }, [carregarMesas]);

  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Enquanto na grade, atualiza sozinho a cada 15s — o garçom não fica
  // precisando puxar pra atualizar pra ver uma mesa que outro colega abriu.
  useEffect(() => {
    if (tela !== 'grade') return;
    const t = setInterval(() => carregarMesas(tipoVenda), 15000);
    return () => clearInterval(t);
  }, [tela, tipoVenda, carregarMesas]);

  const carregarComandaDaMesa = useCallback((mesaId) => {
    return api.get(`/comandas/${mesaId}`)
      .then(r => { setComanda(r.data); cacheComanda(mesaId, r.data); return r.data; })
      .catch(async (err) => {
        if (err.response) { setComanda(null); return null; }
        // Nunca de outra organização (mesmo dispositivo pode operar mais de
        // uma loja no mesmo navegador).
        const cache = await getComandaCache(mesaId, orgId);
        setComanda(cache);
        return cache;
      });
  }, [orgId]);

  const trocarTipoVenda = (tipo) => {
    setTipoVenda(tipo);
    setMesaSelId(null);
    setErro('');
    carregarMesas(tipo);
  };

  const statusMesa = m => m.comanda_status === 'aguardando_fechamento' ? 'aguardando'
    : m.comanda_status === 'aberta' ? 'ocupada'
    : m.estado === 'reservada' ? 'reservada'
    : m.estado === 'limpeza' ? 'limpeza'
    : 'livre';

  const contagem = mesas.reduce((acc, m) => {
    const st = statusMesa(m);
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  const selecionarMesa = (mesa) => {
    setMesaSelId(mesa.id === mesaSelId ? null : mesa.id);
    setErro('');
  };

  // ── Entrar na mesa: livre/reservada abre (cria a comanda); ocupada/
  // aguardando só entra na já existente; limpeza conclui a limpeza ──
  const entrarNaMesaSelecionada = async () => {
    if (!mesaSel) return;
    const st = statusMesa(mesaSel);
    if (st === 'limpeza') { concluirLimpeza(); return; }
    if (st === 'ocupada' || st === 'aguardando') {
      await carregarComandaDaMesa(mesaSel.id);
      setDesconto(0);
      setAbaAtendimento('produtos');
      setTela('atendimento');
      return;
    }
    // livre ou reservada: mostra o formulário rápido (opcional) antes de abrir
    setFormAbertura({ numPessoas: mesaSel.reserva_pessoas ? String(mesaSel.reserva_pessoas) : '', nomeCliente: mesaSel.reserva_nome || '' });
  };

  const concluirLimpeza = async () => {
    if (!mesaSel) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    try {
      await api.patch(`/mesas/${mesaSel.id}/estado`, { estado: 'disponivel' });
      carregarMesas(tipoVenda);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao concluir a limpeza.');
    }
  };

  const confirmarAbertura = async () => {
    if (!mesaSel || abrindoRef.current) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    // Ref (não estado): toque duplo passava duas vezes antes do re-render.
    abrindoRef.current = true;
    setProcessando(true); setErro('');
    try {
      await api.post('/comandas', {
        mesa_id: mesaSel.id,
        num_pessoas: formAbertura.numPessoas ? parseInt(formAbertura.numPessoas) : null,
        cliente_nome: formAbertura.nomeCliente || null,
      });
      carregarMesas(tipoVenda);
      await carregarComandaDaMesa(mesaSel.id);
      setFormAbertura(null);
      setDesconto(0);
      setAbaAtendimento('produtos');
      setTela('atendimento');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao abrir a mesa.');
    } finally {
      setProcessando(false);
      abrindoRef.current = false;
    }
  };

  const voltarParaGrade = () => {
    setTela('grade');
    setMesaSelId(null);
    setComanda(null);
    setItemSelId(null);
    setBusca('');
    setGrupoSelId(null);
    setErro('');
    carregarMesas(tipoVenda);
  };

  const adicionarProduto = async (produto) => {
    if (!comanda || lendoPeso) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    setErro('');
    try {
      let quantidade = 1;
      if (produto.pesado_no_caixa) {
        // Balança fica no PC do caixa, não dá pra alcançar de um celular —
        // peso sempre digitado na mão (mesmo caminho do desktop com a
        // balança desligada).
        const texto = window.prompt(`Peso de ${produto.nome} (kg):`, '');
        if (texto === null) return;
        const peso = parseFloat(String(texto).replace(',', '.'));
        if (!peso || peso <= 0) { setErro('Peso inválido.'); return; }
        quantidade = peso;
      }
      setLendoPeso(true);
      await api.post(`/comandas/${comanda.id}/itens`, { produto_id: produto.id, quantidade });
      await carregarComandaDaMesa(mesaSel.id);
      carregarMesas(tipoVenda);
    } catch (err) {
      setErro(err.response?.data?.erro || err.message || 'Erro ao adicionar o produto.');
    } finally {
      setLendoPeso(false);
    }
  };

  const cancelarItemSelecionado = async (senha_admin) => {
    if (!comanda || !itemSelId) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    try {
      await api.delete(`/comandas/${comanda.id}/itens/${itemSelId}`, senha_admin ? { data: { senha_admin } } : undefined);
      setItemSelId(null);
      setErro('');
      await carregarComandaDaMesa(mesaSel.id);
      carregarMesas(tipoVenda);
    } catch (err) {
      if (err.response?.data?.exige_senha_admin) {
        const item = comanda.itens?.find(i => i.id === itemSelId);
        const senha = await pedirSenhaAdmin(err.response.data.erro, {
          acao: 'excluir_item_comanda_apos_prazo',
          detalhes: item?.descricao || `item ${itemSelId}`,
        });
        if (senha === null) return;
        return cancelarItemSelecionado(senha);
      }
      setErro(err.response?.data?.erro || 'Erro ao cancelar o item.');
    }
  };

  const observarItemSelecionado = async () => {
    if (!comanda || !itemSelId) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    const item = comanda.itens?.find(i => i.id === itemSelId);
    const texto = window.prompt('Observação do item:', item?.observacao || '');
    if (texto === null) return;
    try {
      await api.patch(`/comandas/${comanda.id}/itens/${itemSelId}`, { observacao: texto });
      await carregarComandaDaMesa(mesaSel.id);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar a observação.');
    }
  };

  // Observação do pedido inteiro — sai no fim da impressão, abaixo dos itens.
  const observarPedido = async () => {
    if (!comanda) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    const texto = window.prompt('Observação do pedido (sai no final da impressão):', comanda.observacao || '');
    if (texto === null) return;
    try {
      await api.patch(`/comandas/${comanda.id}/observacao`, { observacao: texto });
      await carregarComandaDaMesa(mesaSel.id);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar a observação.');
    }
  };

  const abrirDesconto = () => {
    const texto = window.prompt('Desconto (R$):', String(desconto || ''));
    if (texto === null) return;
    const v = parseFloat(String(texto).replace(',', '.'));
    setDesconto(isNaN(v) ? 0 : Math.max(0, v));
  };

  const solicitarFechamento = async () => {
    if (!comanda) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    try {
      await api.patch(`/comandas/${comanda.id}/solicitar-fechamento`);
      carregarMesas(tipoVenda);
      await carregarComandaDaMesa(mesaSel.id);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao solicitar fechamento.');
    }
  };

  const cancelarComanda = async (senha_admin, motivo) => {
    if (!comanda) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    const rotulo = tipoVenda === 'mesa' ? 'Mesa' : 'Comanda';
    if (!senha_admin && !window.confirm(`Cancelar a ${rotulo} ${numFmt(mesaSel.numero)}? A ${rotulo.toLowerCase()} volta a ficar Livre e o que foi lançado é perdido.`)) return;
    try {
      await api.post(`/comandas/${comanda.id}/cancelar`, senha_admin ? { senha_admin, motivo } : undefined);
      voltarParaGrade();
    } catch (err) {
      if (err.response?.data?.exige_senha_admin) {
        // Motivo é pedido junto da senha (pedido explícito) — comanda vazia
        // cancela sem nenhum dos dois, só quem tem item lançado exige.
        const motivoDigitado = window.prompt(`${rotulo} tem item(ns) lançado(s) — motivo do cancelamento (mínimo 5 caracteres):`);
        if (motivoDigitado === null) return;
        if (motivoDigitado.trim().length < 5) { setErro('Motivo deve ter no mínimo 5 caracteres.'); return; }
        const senha = await pedirSenhaAdmin(err.response.data.erro, {
          acao: 'cancelar_mesa_com_itens',
          detalhes: `${rotulo} ${numFmt(mesaSel.numero)} — Motivo: ${motivoDigitado.trim()}`,
        });
        if (senha === null) return;
        return cancelarComanda(senha, motivoDigitado.trim());
      }
      setErro(err.response?.data?.erro || 'Erro ao cancelar a mesa.');
    }
  };

  const imprimirPedido = () => {
    if (!comanda || !mesaSel) return;
    const rotulo = tipoVenda === 'mesa' ? 'Mesa' : 'Comanda';
    const numFmtLocal = numFmt(mesaSel.numero);
    const itensImp = comanda.itens || [];
    const subtotalImp = itensImp.reduce((acc, i) => acc + parseFloat(i.valor_total), 0);
    const taxaImp = parseFloat(comanda.taxa_entrega) || 0;
    const itensHtml = itensImp.map(it => `
      <div class="it">
        <p>${parseFloat(it.quantidade)}x ${it.descricao}</p>
        <p class="row"><span>R$ ${fmtBRL(it.valor_unitario)} un.</span><span>R$ ${fmtBRL(it.valor_total)}</span></p>
        ${it.observacao ? `<p style="font-size:12px;">Obs: ${it.observacao}</p>` : ''}
      </div>`).join('');
    // Itens no mesmo tamanho do título (14px) pra cozinha ler de longe;
    // 5mm à esquerda: a térmica do lanchedajack comia ~2 caracteres na borda.
    // largura respeita a margem de segurança do PDV (impressaoMargem.js).
    const larguraCSS = larguraEfetivaCSS('80mm');
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
<title>${rotulo} ${numFmtLocal}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Courier New',Courier,monospace; font-size:11px; font-weight:600; width:${larguraCSS}; padding:6px 6px 6px 5mm; }
  .c { text-align:center; } .b { font-weight:bold; }
  .hr { border:none; border-top:1px dashed #000; margin:5px 0; }
  .row { display:flex; justify-content:space-between; }
  .it { margin:4px 0; font-size:14px; font-weight:bold; } .grand { font-size:13px; }
  @media print { @page { margin:0; size:${larguraCSS} auto; } }
</style></head><body>
  <p class="c b" style="font-size:14px;">${rotulo.toUpperCase()} ${numFmtLocal}</p>
  <p class="c">${new Date().toLocaleString('pt-BR')}</p>
  <hr class="hr">
  ${itensHtml || '<p class="c">Nenhum item lançado.</p>'}
  ${comanda.observacao ? `<hr class="hr"><p style="font-size:13px;font-weight:bold;">OBS. DO PEDIDO: ${comanda.observacao}</p>` : ''}
  <hr class="hr">
  <p class="row b"><span>Subtotal</span><span>R$ ${fmtBRL(subtotalImp)}</span></p>
  ${desconto > 0 ? `<p class="row"><span>Desconto</span><span>- R$ ${fmtBRL(desconto)}</span></p>` : ''}
  ${taxaImp > 0 ? `<p class="row"><span>Taxa de entrega</span><span>+ R$ ${fmtBRL(taxaImp)}</span></p>` : ''}
  <p class="row grand b"><span>TOTAL</span><span>R$ ${fmtBRL(Math.max(0, subtotalImp - desconto) + taxaImp)}</span></p>
  <hr class="hr">
  <p class="c" style="font-size:9px;">Documento sem valor fiscal — conferência do pedido</p>
</body></html>`;
    const win = window.open('', '_blank');
    if (!win) { setErro('Permita pop-ups para imprimir.'); return; }
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    win.focus();
    win.print();
  };

  // ── Finalizar venda: folha simplificada de pagamento, só formas à vista
  // que não exigem cliente identificado (a comanda mobile não faz cadastro
  // de cliente, só nome livre) — crediário/boleto/pontos ficam pro PDV. ──
  const itensAtivos = comanda?.itens || [];
  const subtotal = itensAtivos.reduce((acc, i) => acc + parseFloat(i.valor_total), 0);
  // Taxa de entrega é lançada no F1 do PDV; aqui só entra na conta.
  const taxaEntrega = parseFloat(comanda?.taxa_entrega) || 0;
  const total = Math.max(0, subtotal - desconto) + taxaEntrega;

  const finalizarVenda = async (forma, senha_estoque_insuficiente) => {
    if (!comanda || !itensAtivos.length) return;
    if (!online) { setErro(MSG_OFFLINE); return; }
    let totalPago = total;
    let troco = 0;
    if (forma === 'Dinheiro' && !senha_estoque_insuficiente) {
      const texto = window.prompt('Valor recebido (R$):', fmtBRL(total));
      if (texto === null) return;
      const v = parseFloat(String(texto).replace(',', '.'));
      if (!v || v < total) { setErro('Valor recebido insuficiente.'); return; }
      totalPago = v;
      troco = v - total;
    }
    setFinalizando(true); setErro('');
    try {
      const payload = {
        cliente_id: comanda.cliente_id || null,
        desconto,
        taxa_entrega: taxaEntrega,
        total_pago: totalPago,
        troco,
        observacao: null,
        tipo_fiscal: 'sem',
        comanda_id: comanda.id,
        vendedor_id: usuario?.id || null,
        chave_idempotencia: crypto.randomUUID(),
        itens: itensAtivos.map(it => ({
          produto_id: it.produto_id,
          descricao: it.descricao,
          unidade: it.produto_unidade || it.unidade,
          quantidade: parseFloat(it.quantidade),
          preco_unitario: parseFloat(it.valor_unitario),
          preco_tabela: parseFloat(it.valor_unitario),
          total_item: parseFloat(it.valor_total),
        })),
        pagamentos: [{ forma_pagamento: forma, valor: total, bandeira_id: null, parcelas: 1, conta_id: null }],
      };
      if (senha_estoque_insuficiente) payload.senha_estoque_insuficiente = senha_estoque_insuficiente;
      await api.post('/vendas', payload);
      setPagamentoAberto(false);
      voltarParaGrade();
    } catch (err) {
      if (err.response?.data?.exige_senha_estoque_insuficiente) {
        const senha = await pedirSenhaAdmin(err.response.data.erro, {
          acao: 'estoque_insuficiente_venda', detalhes: `${tipoVenda === 'mesa' ? 'Mesa' : 'Comanda'} ${numFmt(mesaSel.numero)}`,
        });
        if (senha === null) { setFinalizando(false); return; }
        return finalizarVenda(forma, senha);
      }
      setErro(err.response?.data?.erro || 'Erro ao finalizar a venda.');
    } finally {
      setFinalizando(false);
    }
  };

  if (!pronto) return null;

  if (!(usuario?.garcom || usuario?.comanda || modoMobileCompleto(usuario)) || !permitido('/vendas/pdv')) {
    return (
      <div className={s.semAcesso}>
        <p>Seu usuário não tem acesso a Mesas/Comandas.</p>
        <button className={s.sair} onClick={logout}>Sair</button>
      </div>
    );
  }

  const produtosFiltrados = produtos.filter(p => {
    if (grupoSelId && p.grupo_id !== grupoSelId) return false;
    if (busca && !p.nome.toLowerCase().includes(busca.toLowerCase())
        && !String(p.codigo_interno).includes(busca)
        && !(p.codigo_barras || '').includes(busca)) return false;
    return true;
  });

  const comandaAberta = comanda && ['aberta', 'aguardando_fechamento'].includes(comanda.status);
  const ehMesa = tipoVenda === 'mesa';
  const rotulo = ehMesa ? 'Mesa' : 'Comanda';
  const statusesResumo = ehMesa
    ? ['livre', 'ocupada', 'aguardando', 'reservada', 'limpeza']
    : ['livre', 'ocupada', 'aguardando'];
  const statusSel = mesaSel ? statusMesa(mesaSel) : null;

  return (
    <div className={s.wrap}>
      <header className={s.header}>
        <BotaoMenuMobile />
        <span className={s.marca}>{BRAND_MARCA_CURTA}</span>
        {!online && <span className={s.badgeOffline}>🟡 Offline</span>}
        <span className={s.usuario}>{usuario?.nome}</span>
        <button className={s.sair} onClick={logout}>Sair</button>
      </header>

      {erro && <div className={s.erroBar} onClick={() => setErro('')}>{erro}</div>}

      {tela === 'grade' && (
        <div className={s.grade}>
          <div className={s.tipoVendaRow}>
            <button type="button" className={`${s.tipoVendaBtn} ${tipoVenda === 'mesa' ? s.tipoVendaBtnAtivo : ''}`}
              onClick={() => trocarTipoVenda('mesa')}>🍽️ MESA</button>
            <button type="button" className={`${s.tipoVendaBtn} ${tipoVenda === 'individual' ? s.tipoVendaBtnAtivo : ''}`}
              onClick={() => trocarTipoVenda('individual')}>🧾 COMANDA</button>
          </div>

          <div className={s.legenda}>
            {statusesResumo.map(st => (
              <div key={st} className={s.legendaItem}>
                <span className={s.bolinha} style={{ background: CORES[st] }} />
                <span>{LABEL_STATUS[st]}</span>
                <b>{contagem[st] || 0}</b>
              </div>
            ))}
          </div>

          <div className={s.mesasGradeWrap}>
            <div className={s.mesasGradeInner}>
              {mesas.map(m => {
                const st = statusMesa(m);
                return (
                  <div key={m.id}
                    className={`${s.mesaCard} ${m.id === mesaSelId ? s.mesaCardSel : ''}`}
                    style={{ background: CORES[st] }}
                    onClick={() => selecionarMesa(m)}>
                    <span className={s.mesaNumero}>{numFmt(m.numero)}</span>
                    {st === 'livre' && <span className={s.mesaValor}>Livre</span>}
                    {(st === 'ocupada' || st === 'aguardando') && <span className={s.mesaValor}>{fmtBRL(m.valor_parcial)}</span>}
                    {st === 'reservada' && <span className={s.mesaValor}>Reservada</span>}
                    {st === 'limpeza' && <span className={s.mesaValor}>Limpeza</span>}
                  </div>
                );
              })}
              {mesas.length === 0 && (
                <div className={s.mesasVazio}>Nenhuma {rotulo.toLowerCase()} cadastrada.</div>
              )}
            </div>
          </div>

          <div className={s.footerAcao}>
            <button
              type="button"
              className={s.btnAbrirMesa}
              disabled={!mesaSel || processando}
              onClick={entrarNaMesaSelecionada}
            >
              {!mesaSel ? `Selecione uma ${rotulo.toLowerCase()}`
                : statusSel === 'limpeza' ? '✅ Concluir Limpeza'
                : (statusSel === 'ocupada' || statusSel === 'aguardando') ? `VER ${rotulo.toUpperCase()} ${numFmt(mesaSel.numero)}`
                : `ABRIR ${rotulo.toUpperCase()} ${numFmt(mesaSel.numero)}`}
            </button>
          </div>
        </div>
      )}

      {tela === 'atendimento' && mesaSel && (
        <div className={s.atendimento}>
          <div className={s.atendimentoHeader}>
            <button type="button" className={s.btnVoltar} onClick={voltarParaGrade}>←</button>
            <div className={s.atendimentoTitulo}>
              <span>{ehMesa ? '🍽️' : '🧾'} {rotulo} {numFmt(mesaSel.numero)}</span>
              {comanda?.aberta_em && <span className={s.atendimentoTempo}>🕒 {elapsedStr(comanda.aberta_em, agora)}</span>}
            </div>
            <div className={s.atendimentoTotal}>{fmtBRL(total)}</div>
          </div>

          <div className={s.abas}>
            <button type="button" className={`${s.abaBtn} ${abaAtendimento === 'produtos' ? s.abaBtnAtiva : ''}`}
              onClick={() => setAbaAtendimento('produtos')}>🛒 Produtos</button>
            <button type="button" className={`${s.abaBtn} ${abaAtendimento === 'comanda' ? s.abaBtnAtiva : ''}`}
              onClick={() => setAbaAtendimento('comanda')}>🧾 Comanda ({itensAtivos.length})</button>
          </div>

          {abaAtendimento === 'produtos' ? (
            <>
              <div className={s.buscaBar}>
                <input ref={buscaRef} className={s.buscaInput} placeholder="Buscar produto..."
                  value={busca} onChange={e => setBusca(e.target.value)} />
              </div>
              <div className={s.categorias}>
                <button type="button" className={`${s.categoriaBtn} ${!grupoSelId ? s.categoriaBtnAtiva : ''}`}
                  onClick={() => setGrupoSelId(null)}>Todos</button>
                {grupos.map(g => (
                  <button key={g.id} type="button" className={`${s.categoriaBtn} ${grupoSelId === g.id ? s.categoriaBtnAtiva : ''}`}
                    onClick={() => setGrupoSelId(g.id)}>{g.nome}</button>
                ))}
              </div>
              <div className={s.produtosGradeWrap}>
                {produtosFiltrados.length === 0 ? (
                  <div className={s.produtosVazio}>Nenhum produto encontrado.</div>
                ) : (
                  <div className={s.produtosGrade}>
                    {produtosFiltrados.map(p => (
                      <div key={p.id} className={s.produtoCard} onClick={() => adicionarProduto(p)}>
                        {p.foto_url ? (
                          <img className={s.produtoFoto} src={p.foto_url} alt={p.nome} />
                        ) : (
                          <span className={s.produtoFotoVazia}>📦</span>
                        )}
                        <span className={s.produtoNome}>{p.nome}</span>
                        <span className={s.produtoPreco}>{fmtBRL(p.preco_venda)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className={s.itensLista}>
                {itensAtivos.length === 0 ? (
                  <div className={s.itensVazio}>Nenhum item lançado ainda — vá em Produtos pra começar.</div>
                ) : itensAtivos.map(it => (
                  <div key={it.id} className={`${s.itemLinha} ${it.id === itemSelId ? s.itemLinhaSel : ''}`}
                    onClick={() => setItemSelId(it.id === itemSelId ? null : it.id)}>
                    <span className={s.itemInfo}>
                      <span className={s.itemDescricao}>{parseFloat(it.quantidade)}x {it.descricao}</span>
                      {it.observacao && <span className={s.itemObs}>{it.observacao}</span>}
                    </span>
                    <span className={s.itemValor}>{fmtBRL(it.valor_total)}</span>
                    <button type="button" className={s.itemDelete} title="Remover item"
                      onClick={(e) => { e.stopPropagation(); setItemSelId(it.id); cancelarItemSelecionado(); }}>🗑️</button>
                  </div>
                ))}
              </div>

              <button type="button" className={s.obsBtn} disabled={!itemSelId} onClick={observarItemSelecionado}>
                📝 Observação no item selecionado
              </button>
              <button type="button" className={s.obsBtn} onClick={observarPedido} disabled={!comanda}>
                📝 Observação do pedido
              </button>
              {comanda?.observacao && <div className={s.obsPedido}>📝 {comanda.observacao}</div>}

              <div className={s.totaisBox}>
                <div className={s.totaisLinha}><span>Subtotal</span><span>{fmtBRL(subtotal)}</span></div>
                <div className={s.totaisLinha} onClick={abrirDesconto}><span>Desconto</span><span>{fmtBRL(desconto)}</span></div>
                {taxaEntrega > 0 && <div className={s.totaisLinha}><span>🛵 Taxa de entrega</span><span>{fmtBRL(taxaEntrega)}</span></div>}
                <div className={`${s.totaisLinha} ${s.totaisTotal}`}><span>💲 Total</span><span>{fmtBRL(total)}</span></div>
              </div>

              <div className={s.botoesFooter}>
                {comanda?.status === 'aberta' && (
                  <button type="button" className={s.btnSolicitar} onClick={solicitarFechamento}>
                    🔔 Solicitar Fechamento
                  </button>
                )}
                <button type="button" className={s.btnFinalizar} disabled={!itensAtivos.length}
                  onClick={() => setPagamentoAberto(true)}>
                  ✓ Finalizar Venda (F9)
                </button>
                <button type="button" className={s.btnImprimir} disabled={!itensAtivos.length} onClick={imprimirPedido}>
                  🖨️ Imprimir Pedido
                </button>
                <button type="button" className={s.btnCancelar} onClick={() => cancelarComanda()}>
                  ✕ Cancelar {rotulo}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {formAbertura && (
        <div className={s.overlay} onClick={() => setFormAbertura(null)}>
          <div className={s.overlayBox} onClick={e => e.stopPropagation()}>
            <div className={s.overlayTitulo}>Abrir {rotulo} {numFmt(mesaSel?.numero)}</div>
            <label className={s.overlayCampo}>
              Quantas pessoas? (opcional)
              <input type="number" min="1" value={formAbertura.numPessoas}
                onChange={e => setFormAbertura({ ...formAbertura, numPessoas: e.target.value })} />
            </label>
            <label className={s.overlayCampo}>
              Nome do cliente
              <input type="text" value={formAbertura.nomeCliente}
                onChange={e => setFormAbertura({ ...formAbertura, nomeCliente: e.target.value })} />
            </label>
            {erro && <div className={s.erroBar}>{erro}</div>}
            <div className={s.overlayBotoes}>
              <button type="button" className={s.btnAbrirMesa} disabled={processando} onClick={confirmarAbertura}>
                {processando ? 'Abrindo…' : `ABRIR ${rotulo.toUpperCase()}`}
              </button>
              <button type="button" className={s.btnCancelarLink} onClick={() => setFormAbertura(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {pagamentoAberto && (
        <div className={s.overlay} onClick={() => !finalizando && setPagamentoAberto(false)}>
          <div className={s.overlayBox} onClick={e => e.stopPropagation()}>
            <div className={s.overlayTitulo}>Finalizar Venda — {fmtBRL(total)}</div>
            <div className={s.formasPagamento}>
              {FORMAS_PAGAMENTO.map(forma => (
                <button key={forma} type="button" className={s.formaBtn} disabled={finalizando}
                  onClick={() => finalizarVenda(forma)}>
                  {forma}
                </button>
              ))}
            </div>
            {erro && <div className={s.erroBar}>{erro}</div>}
            <div className={s.overlayBotoes}>
              <button type="button" className={s.btnCancelarLink} disabled={finalizando} onClick={() => setPagamentoAberto(false)}>
                {finalizando ? 'Finalizando…' : 'Cancelar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
